import {useEffect, useState} from "react";
import {Navigate, useLocation, useParams} from "react-router-dom";
import {adminLoginPath} from "../Admin/adminRedirect";
import WebinarBroadcastEmailForm from "./WebinarBroadcastEmailForm";
import WebinarCampaignControl from "./WebinarCampaignControl";
import WebinarCampaignList from "./WebinarCampaignList";
import "./WebinarEmailReport.css";

const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL ||
    (window.location.hostname === "localhost" ? "http://localhost:8000" : "");

function apiUrl(path) {
    return `${API_BASE_URL}${path}`;
}

function formatDateTime(value) {
    if (!value) return "غير محدد";

    return new Intl.DateTimeFormat("ar", {
        dateStyle: "full",
        timeStyle: "short",
        numberingSystem: "latn",
    }).format(new Date(value));
}

function formatCount(value) {
    const numericValue = Number(value);

    return new Intl.NumberFormat("en-US").format(Number.isFinite(numericValue) ? numericValue : 0);
}

function WebinarRegistrantModal({registrants = [], isOpen, onClose}) {
    useEffect(() => {
        if (!isOpen) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div
            className="email-report-registrants-modal"
            role="presentation"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <section
                className="email-report-registrants-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="email-report-registrants-title"
            >
                <header className="email-report-registrants-heading">
                    <div>
                        <p>المسجلون</p>
                        <h2 id="email-report-registrants-title">أسماء وإيميلات المسجلين</h2>
                    </div>
                    <button
                        className="email-report-registrants-close"
                        type="button"
                        aria-label="إغلاق قائمة المسجلين"
                        onClick={onClose}
                    >
                        ×
                    </button>
                </header>

                {registrants.length === 0 ? (
                    <p className="email-report-registrants-empty">لا يوجد مسجلون لهذا الويبينار حتى الآن.</p>
                ) : (
                    <ul className="email-report-registrants-list">
                        {registrants.map((registrant, index) => (
                                <li key={`${registrant.email || "registrant"}-${index}`}>
                                    <span>{registrant.name || "***"}</span>
                                    <span dir="ltr">{registrant.email || "غير متوفر"}</span>
                                </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}

function RegistrantCountButton({count, onClick, isDisabled}) {
    return (
        <button
            className="email-report-registration-trigger"
            type="button"
            onClick={onClick}
            disabled={isDisabled}
            aria-haspopup="dialog"
        >
            {formatCount(count)}
        </button>
    );
}

export default function WebinarEmailReport() {
    const {webinarIdentifier = ""} = useParams();
    const location = useLocation();
    const token = localStorage.getItem("webinarAdminToken") || "";
    const [report, setReport] = useState(null);
    const [requiresLogin, setRequiresLogin] = useState(false);
    const [status, setStatus] = useState({type: "loading", message: "جاري تحميل التقرير..."});
    const [reportRefreshKey, setReportRefreshKey] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isRegistrantModalOpen, setIsRegistrantModalOpen] = useState(false);

    useEffect(() => {
        if (!token) {
            return undefined;
        }

        const controller = new AbortController();

        if (reportRefreshKey > 0) {
            setIsRefreshing(true);
        }

        const loadReport = async () => {
            try {
                const response = await fetch(
                    apiUrl(`/api/admin/webinars/${encodeURIComponent(webinarIdentifier)}/email-report`),
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        signal: controller.signal,
                    }
                );
                const data = await response.json();

                if (!response.ok) {
                    const error = new Error(data.message || "تعذر تحميل تقرير الويبينار.");
                    error.status = response.status;
                    throw error;
                }

                setReport(data.report || null);
                setStatus({type: "ready", message: ""});
            } catch (error) {
                if (error.name === "AbortError") return;

                if (error.status === 401 || responseRequiresLogin(error.message)) {
                    localStorage.removeItem("webinarAdminToken");
                    setRequiresLogin(true);
                    return;
                }

                setStatus({
                    type: "error",
                    message: error.message || "تعذر تحميل تقرير الويبينار.",
                });
            } finally {
                if (!controller.signal.aborted) {
                    setIsRefreshing(false);
                }
            }
        };

        loadReport();

        return () => controller.abort();
    }, [token, webinarIdentifier, reportRefreshKey]);

    if (!token || requiresLogin) {
        return (
            <Navigate
                to={adminLoginPath(location.pathname, location.search, location.hash)}
                replace
            />
        );
    }

    const refreshReport = () => setReportRefreshKey((current) => current + 1);
    const registrants = report?.registrants || [];

    return (
        <main className="WebinarEmailReport">
            <section className="email-report-shell" aria-labelledby="email-report-title">
                <header className="email-report-header">
                    <div>
                        <p className="email-report-eyebrow">تقارير الويبينار</p>
                        <h1 id="email-report-title">تقرير التسجيل والإلغاء</h1>
                    </div>
                    <a className="email-report-back" href="/admin">
                        العودة إلى الإدارة
                    </a>
                </header>

                {status.type !== "ready" ? (
                    <div className={`email-report-state email-report-state--${status.type}`} role="status">
                        <p>{status.message}</p>
                    </div>
                ) : report ? (
                    <>
                        <dl className="email-report-grid">
                            <div className="email-report-card email-report-card--wide">
                                <dt>عنوان الويبينار</dt>
                                <dd>{report.title || "بدون عنوان"}</dd>
                            </div>
                            <div className="email-report-card email-report-card--wide">
                                <dt>تاريخ الويبينار</dt>
                                <dd>{formatDateTime(report.startsAt)}</dd>
                            </div>
                            <div className="email-report-card email-report-card--registration">
                                <dt>عدد المسجلين</dt>
                                <dd>
                                    <RegistrantCountButton
                                        count={report.registrationCount}
                                        onClick={() => setIsRegistrantModalOpen(true)}
                                        isDisabled={registrants.length === 0}
                                    />
                                </dd>
                            </div>
                            <div className="email-report-card email-report-card--cancellation">
                                <dt>عدد الإلغاءات</dt>
                                <dd>{formatCount(report.cancellationCount)}</dd>
                            </div>
                        </dl>
                        <WebinarCampaignList
                            campaigns={report.campaigns || []}
                            isRefreshing={isRefreshing}
                            onRefresh={refreshReport}
                        />
                        <WebinarCampaignControl
                            webinarIdentifier={webinarIdentifier}
                            webinarTitle={report.title}
                            report={report}
                            onReportChange={(nextReport) => {
                                setReport((currentReport) => mergeCampaignAnalytics(currentReport, nextReport));
                            }}
                            onUnauthorized={() => {
                                localStorage.removeItem("webinarAdminToken");
                                setRequiresLogin(true);
                            }}
                        />
                        <WebinarBroadcastEmailForm
                            webinarIdentifier={webinarIdentifier}
                            webinarTitle={report.title}
                            recipientCount={report.registrationCount ?? 0}
                            emailsSuspended={report.emailsSuspended === true}
                            onCampaignSent={refreshReport}
                            onUnauthorized={() => {
                                localStorage.removeItem("webinarAdminToken");
                                setRequiresLogin(true);
                            }}
                        />
                        <WebinarRegistrantModal
                            registrants={registrants}
                            isOpen={isRegistrantModalOpen}
                            onClose={() => setIsRegistrantModalOpen(false)}
                        />
                    </>
                ) : (
                    <div className="email-report-state email-report-state--error" role="status">
                        <p>لم يتم العثور على بيانات التقرير.</p>
                    </div>
                )}
            </section>
        </main>
    );
}

function mergeCampaignAnalytics(previousReport, nextReport) {
    if (!nextReport || !Array.isArray(nextReport.campaigns)) {
        return nextReport;
    }

    const previousCampaigns = Array.isArray(previousReport?.campaigns)
        ? previousReport.campaigns
        : [];
    const previousAnalytics = new Map(
        previousCampaigns
            .filter((campaign) => campaign?.analytics)
            .map((campaign) => [campaign.campaignId || campaign.key, campaign.analytics])
    );

    return {
        ...nextReport,
        campaigns: nextReport.campaigns.map((campaign) => {
            if (campaign?.analytics) {
                return campaign;
            }

            const key = campaign?.campaignId || campaign?.key;
            const analytics = previousAnalytics.get(key);

            return analytics ? {...campaign, analytics} : campaign;
        }),
    };
}

function responseRequiresLogin(message) {
    return message.includes("تسجيل الدخول") || message.includes("جلسة الإدارة");
}
